// src/components/store/sections/ai-recommender/AiRecommenderCurvedCards.tsx
"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { 
  Sparkles, 
  ShoppingCart, 
  Zap, 
  Check, 
  ChevronLeft, 
  ChevronRight, 
  ShoppingBag, 
  Loader2 
} from "lucide-react";
import { useCart } from "@/context/CartContext";

interface ProductItem {
  id: number;
  name: string;
  slug: string;
  price: number | string;
  compare_price?: number | string | null;
  primary_image?: string | null;
  image?: string | null;
  description?: string;
  category_name?: string;
  unit?: string;
}

interface AiRecommenderProps {
  products?: ProductItem[];
  title?: string;
  subtitle?: string;
  primaryColor?: string;
  subdomain?: string;
}

// 🌟 ১০০% স্বচ্ছ ব্যাকগ্রাউন্ড ক্যানভাস সাব-কম্পোনেন্ট
function CleanTransparentProductImage({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [cleanSrc, setCleanSrc] = useState<string>(src);
  const [isReady, setIsReady] = useState<boolean>(false);

  useEffect(() => {
    if (!src) return;
    let isMounted = true;

    fetch(src, { mode: "cors" })
      .then((res) => {
        if (!res.ok) throw new Error("Image fetch failed");
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const img = new window.Image();
        img.src = blobUrl;

        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            const w = img.naturalWidth || img.width;
            const h = img.naturalHeight || img.height;
            canvas.width = w;
            canvas.height = h;

            const ctx = canvas.getContext("2d", { willReadFrequently: true });
            if (!ctx) {
              if (isMounted) {
                setCleanSrc(src);
                setIsReady(true);
              }
              return;
            }

            ctx.drawImage(img, 0, 0);
            const imgData = ctx.getImageData(0, 0, w, h);
            const data = imgData.data;

            const visited = new Uint8Array(w * h);
            const queue: number[] = [];

            for (let x = 0; x < w; x++) {
              queue.push(x, 0);
              queue.push(x, h - 1);
              visited[x] = 1;
              visited[(h - 1) * w + x] = 1;
            }
            for (let y = 1; y < h - 1; y++) {
              queue.push(0, y);
              queue.push(w - 1, y);
              visited[y * w] = 1;
              visited[y * w + (w - 1)] = 1;
            }

            let qHead = 0;
            while (qHead < queue.length) {
              const cx = queue[qHead++];
              const cy = queue[qHead++];
              const idx = (cy * w + cx) * 4;

              const r = data[idx];
              const g = data[idx + 1];
              const b = data[idx + 2];

              const colorDiff = Math.max(Math.abs(r - g), Math.abs(g - b), Math.abs(r - b));
              const luminance = 0.299 * r + 0.587 * g + 0.114 * b;

              const isStudioBg =
                (luminance > 140 && colorDiff < 35) ||
                (luminance > 205) ||
                (r > 195 && g > 195 && b > 195);

              if (isStudioBg) {
                data[idx + 3] = 0;

                const neighbors = [
                  [cx + 1, cy],
                  [cx - 1, cy],
                  [cx, cy + 1],
                  [cx, cy - 1],
                ];

                for (const [nx, ny] of neighbors) {
                  if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
                    const nPos = ny * w + nx;
                    if (!visited[nPos]) {
                      visited[nPos] = 1;
                      queue.push(nx, ny);
                    }
                  }
                }
              }
            }

            ctx.putImageData(imgData, 0, 0);
            const transparentPng = canvas.toDataURL("image/png");
            if (isMounted) {
              setCleanSrc(transparentPng);
              setIsReady(true);
            }
          } catch {
            if (isMounted) {
              setCleanSrc(src);
              setIsReady(true);
            }
          } finally {
            URL.revokeObjectURL(blobUrl);
          }
        };

        img.onerror = () => {
          if (isMounted) {
            setCleanSrc(src);
            setIsReady(true);
          }
          URL.revokeObjectURL(blobUrl);
        };
      })
      .catch(() => {
        if (isMounted) {
          setCleanSrc(src);
          setIsReady(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [src]);

  return (
    <img
      src={cleanSrc}
      alt={alt}
      className={`${className || ""} ${isReady ? "opacity-100" : "opacity-95"} transition-opacity duration-300`}
      style={{
        mixBlendMode: "multiply",
      }}
    />
  );
}

export default function AiRecommenderCurvedCards({
  products = [],
  title = "Recommended For You",
  subtitle = "আপনার পছন্দ ও ট্রেন্ডিং আইটেমের ওপর ভিত্তি করে কৃত্রিম বুদ্ধিমত্তা চালিত বাছাই",
  primaryColor = "#6C28FE",
  subdomain = "nextlook",
}: AiRecommenderProps) {
  const router = useRouter();
  const { addToCart } = useCart();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [addedIds, setAddedIds] = useState<Record<number, boolean>>({});
  const [buyingId, setBuyingId] = useState<number | null>(null);

  const resolveImageUrl = useCallback(
    (url?: string | null) => {
      if (!url) return null;
      const path = url.trim();
      if (path.startsWith("http://") || path.startsWith("https://")) return path;
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      const base = isLocal ? `http://${subdomain}.localhost:8000` : "";
      return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
    },
    [subdomain]
  );

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const distance = 340;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -distance : distance,
        behavior: "smooth",
      });
    }
  };

  // 🌟 ADD TO CART বাটন হ্যান্ডলার
  const handleAddToCart = (e: React.MouseEvent, prod: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();

    setAddedIds((prev) => ({ ...prev, [prod.id]: true }));
    setTimeout(() => {
      setAddedIds((prev) => ({ ...prev, [prod.id]: false }));
    }, 2000);

    const fullImg = resolveImageUrl(prod.primary_image || prod.image);
    const itemObj = {
      id: prod.id,
      product_id: prod.id,
      name: prod.name,
      price: Number(prod.price || 0),
      image: fullImg,
      quantity: 1,
      unit: prod.unit || "1 pc",
    };

    const storageKeys = [
      "cart",
      "cart_items",
      `cart_${subdomain}`,
      `xylo_cart_${subdomain}`,
      `soft_cart_${subdomain}`,
      "soft_cart",
      "xylo_cart",
    ];

    storageKeys.forEach((key) => {
      try {
        const raw = localStorage.getItem(key);
        let list: any[] = [];
        if (raw) {
          try {
            list = JSON.parse(raw);
          } catch {}
        }
        if (!Array.isArray(list)) list = [];
        const idx = list.findIndex((x: any) => String(x.id) === String(prod.id));
        if (idx > -1) {
          list[idx].quantity = (Number(list[idx].quantity) || 1) + 1;
        } else {
          list.push({ ...itemObj });
        }
        localStorage.setItem(key, JSON.stringify(list));
      } catch {}
    });

    if (typeof addToCart === "function") {
      addToCart(itemObj, 1);
    }

    window.dispatchEvent(new CustomEvent("addToCart", { detail: itemObj }));
    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));
    window.dispatchEvent(new CustomEvent("openCartDrawer"));
  };

  // 🌟 BUY NOW বাটন হ্যান্ডলার
  const handleBuyNow = (e: React.MouseEvent, prod: ProductItem) => {
    e.preventDefault();
    e.stopPropagation();

    setBuyingId(prod.id);
    const fullImg = resolveImageUrl(prod.primary_image || prod.image);

    const singleItemObj = {
      id: prod.id,
      product_id: prod.id,
      name: prod.name,
      title: prod.name,
      price: Number(prod.price || 0),
      image: fullImg,
      quantity: 1,
      unit: prod.unit || "1 pc",
    };

    try {
      sessionStorage.setItem("direct_checkout_item", JSON.stringify(singleItemObj));
    } catch {}

    const storageKeys = [
      "cart",
      "cart_items",
      `cart_${subdomain}`,
      `xylo_cart_${subdomain}`,
      `soft_cart_${subdomain}`,
      "soft_cart",
      "xylo_cart",
    ];

    storageKeys.forEach((key) => {
      try {
        localStorage.setItem(key, JSON.stringify([singleItemObj]));
      } catch {}
    });

    window.dispatchEvent(new Event("cartUpdated"));
    window.dispatchEvent(new Event("storage"));

    router.push(`/checkout?buy_now=true&product_id=${prod.id}`);
  };

  const getCardAccentColor = (index: number) => {
    const palette = [
      primaryColor,
      "#FF6A1A",
      "#10B981",
      "#0EA5E9",
      "#F59E0B",
      "#8B5CF6",
    ];
    return palette[index % palette.length];
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 my-10 font-sans relative">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 pb-3 border-b border-slate-100 gap-4">
        <div>
          <div
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-wider mb-2 border shadow-xs"
            style={{
              backgroundColor: `${primaryColor}12`,
              borderColor: `${primaryColor}30`,
              color: primaryColor,
            }}
          >
            <Sparkles className="w-3.5 h-3.5 animate-pulse" style={{ color: primaryColor }} />
            <span>AI Smart Recommendations</span>
          </div>

          <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            {title}
          </h2>
          <p className="text-xs text-slate-500 mt-1 max-w-xl">
            {subtitle}
          </p>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            type="button"
            onClick={() => handleScroll("left")}
            className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-sm flex items-center justify-center transition-all hover:scale-105 active:scale-95 cursor-pointer"
            title="Scroll Left"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={() => handleScroll("right")}
            className="w-10 h-10 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 shadow-sm flex items-center justify-center transition-all hover:scale-105 active:scale-95 cursor-pointer"
            title="Scroll Right"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="flex gap-6 overflow-x-auto scroll-smooth py-8 px-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        {products.map((prod, index) => {
          const cardAccent = getCardAccentColor(index);
          const isAdded = addedIds[prod.id];
          const isBuying = buyingId === prod.id;
          const fullImgUrl = resolveImageUrl(prod.primary_image || prod.image);
          const targetUrl = `/products/${prod.slug || prod.id}`;

          const nameWords = prod.name.trim().split(" ");
          const firstLine = nameWords.slice(0, 1).join(" ");
          const secondLine = nameWords.slice(1, 3).join(" ") || "COLLECTION";

          return (
            <div
              key={prod.id}
              className="relative min-w-[290px] sm:min-w-[320px] max-w-[320px] flex-shrink-0 bg-white rounded-[36px] p-6 shadow-[0_16px_40px_rgba(0,0,0,0.06)] hover:shadow-[0_28px_60px_rgba(0,0,0,0.12)] transition-all duration-500 flex flex-col justify-between border border-slate-100/90 group select-none overflow-visible"
            >
              {/* ১. টপ রাইট ডায়াগোনাল কালার স্লাইস */}
              <div
                className="absolute top-0 right-0 w-[70%] h-[62%] rounded-tr-[36px] overflow-hidden pointer-events-none transition-transform duration-500 group-hover:scale-[1.02] z-0"
                style={{
                  backgroundColor: cardAccent,
                  clipPath: "polygon(28% 0%, 100% 0%, 100% 88%, 0% 100%)",
                }}
              >
                <div className="absolute top-5 right-5 text-right font-sans">
                  <span className="text-white text-2xl sm:text-3xl font-black tracking-tight drop-shadow-md">
                    ৳{Math.round(Number(prod.price || 0)).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* ২. ব্যাকগ্রাউন্ড জিওমেট্রিক লাইন */}
              <div className="absolute top-6 left-6 w-[70%] h-[45%] pointer-events-none opacity-20 z-0">
                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id={`grid-scroll-${prod.id}`} width="28" height="28" patternUnits="userSpaceOnUse">
                      <path d="M 0 28 L 28 0 M 0 0 L 28 28" fill="none" stroke="#64748b" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill={`url(#grid-scroll-${prod.id})`} />
                </svg>
              </div>

              {/* 🌟 ৩. ৩ডি ওয়ার্পড ও বড় ফ্লোটিং প্রোডাক্ট ইমেজ (ডিটেইলস পেজে ক্লিকযোগ্য) */}
              <div
                className="relative w-full h-64 flex flex-col items-center justify-center my-1 pointer-events-auto z-20"
                style={{ perspective: "1000px" }}
              >
                <Link
                  href={targetUrl}
                  className="w-full h-full flex flex-col items-center justify-center transition-all duration-500 group-hover:-translate-y-4 cursor-pointer"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  {fullImgUrl ? (
                    <>
                      <div
                        className="relative w-full h-56 flex items-center justify-center transition-all duration-500 ease-out group-hover:scale-110"
                        style={{
                          transform: "rotateY(-10deg) rotateX(8deg) translateZ(28px)",
                          transformStyle: "preserve-3d",
                        }}
                      >
                        <CleanTransparentProductImage
                          src={fullImgUrl}
                          alt={prod.name}
                          className="max-h-52 sm:max-h-56 max-w-[96%] object-contain drop-shadow-[0_24px_28px_rgba(0,0,0,0.45)] drop-shadow-[0_12px_12px_rgba(0,0,0,0.25)] transition-transform duration-500"
                        />
                      </div>

                      <div
                        className="w-44 h-4 mx-auto rounded-[100%] blur-md -mt-3 transition-all duration-500 group-hover:scale-125 group-hover:opacity-60 pointer-events-none"
                        style={{
                          background: "radial-gradient(ellipse at center, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0) 75%)",
                        }}
                      />
                    </>
                  ) : (
                    <div className="w-32 h-32 rounded-2xl bg-white/80 shadow-md flex flex-col items-center justify-center text-slate-400 gap-1 border border-slate-100">
                      <ShoppingBag className="w-10 h-10 opacity-40" />
                      <span className="text-[11px] font-bold">No Image</span>
                    </div>
                  )}
                </Link>
              </div>

              {/* 🌟 ৪. প্রোডাক্ট টাইটেল ও বিবরণ (ডিটেইলস পেজে ক্লিকযোগ্য) */}
              <div className="mt-2 flex-1 flex flex-col justify-end relative z-10">
                <Link href={targetUrl} className="block cursor-pointer">
                  <h3
                    className="text-base sm:text-lg font-black uppercase tracking-wider leading-none truncate"
                    style={{ color: cardAccent }}
                  >
                    {firstLine}
                  </h3>
                  <h4 className="text-base sm:text-lg font-black uppercase tracking-wider text-slate-800 leading-tight truncate">
                    {secondLine}
                  </h4>
                </Link>

                <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-2 line-clamp-2">
                  {prod.description || `${prod.category_name || "Premium"} authentic quality item, rapid home delivery guaranteed.`}
                </p>

                {/* 🌟 ৫. অ্যাকশন বাটনসমূহ (সম্পূর্ণ সক্রিয়) */}
                <div className="flex items-center gap-2 mt-5 relative z-20">
                  <button
                    type="button"
                    onClick={(e) => handleBuyNow(e, prod)}
                    style={{ backgroundColor: cardAccent }}
                    className="flex-1 py-2.5 px-3 rounded-full text-white font-black text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5 shadow-md hover:brightness-105 active:scale-95 transition-all cursor-pointer"
                  >
                    {isBuying ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Zap className="w-3.5 h-3.5 fill-current" />
                    )}
                    <span>{isBuying ? "WAIT..." : "BUY NOW"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => handleAddToCart(e, prod)}
                    className={`flex-1 py-2.5 px-3 rounded-full font-black text-[11px] uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all active:scale-95 cursor-pointer shadow-md ${
                      isAdded
                        ? "bg-emerald-600 text-white shadow-emerald-600/30"
                        : "bg-[#181a20] hover:bg-black text-white"
                    }`}
                  >
                    {isAdded ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-white" />
                        <span>ADDED</span>
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-3.5 h-3.5 text-white" />
                        <span>ADD TO CART</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* ৬. ডেকোরেটিভ ফ্লোটিং ডট */}
              <div
                className="absolute -bottom-2.5 left-5 w-8 h-8 rounded-full shadow-lg pointer-events-none transition-transform group-hover:scale-110 z-10"
                style={{
                  backgroundColor: cardAccent,
                  boxShadow: `0 8px 16px ${cardAccent}40`,
                }}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}